import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useState,
  useCallback,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useSelector, useDispatch } from "react-redux";
import { selectRoyalCollectCurrentRound } from "../../State/selectors";
import { reportRoyalCollectSymbolReady } from "../../State/actions";
import { symbolData, isCoinSymbol, isRoyalSymbol } from "../../SymbolData";
import { Settings } from "../../settings";
import { formatToCustomLocale } from "../../utilities";
import store from "../../State/Store/store";
import * as PIXI from "pixi.js";
import RoyalCollectSymbolController from "./RoyalCollectSymbolController";
import { useRoyalCollectControl } from "../../Contexts/RoyalCollectControlContext";

// Match ReelControllerV2 constants exactly
const COIN_VALUE_Y_OFFSET = 82.67 / 338; // ≈ 0.2446 of symbolHeight

// Visible symbol index in the 4-sprite buffer (for spin container offset)
const VISIBLE_SYMBOL_INDEX = 2;

/**
 * RoyalCollectSymbol
 *
 * Individual symbol cell for the Royal Collect feature.
 * Renders static textures + coin value text using the exact same logic
 * as ReelControllerV2._setSpriteTexture and _calculateSymbolScale.
 *
 * Blanks (symbolId === 0) additionally create a RoyalCollectSymbolController
 * for ticker-based spinning.
 *
 * Coins show the spine-extracted texture + BitmapText value.
 */
const RoyalCollectSymbol = forwardRef(
  (
    {
      symbolWidth,
      symbolHeight,
      index,
      reelIndex,
      reelSymbolsData,
      symbolId,
      symbolsSpriteSheet,
      symbolsSpriteSheetBlurred,
    },
    ref,
  ) => {
    const symbolsContainerRef = useRef(null);
    const spinContainerRef = useRef(null);
    const maskRef = useRef(null);
    const staticSpriteRef = useRef(null);
    const valueTextRef = useRef(null);
    const controllerRef = useRef(null);
    const [maskReady, setMaskReady] = useState(false);
    const reportedReadyRef = useRef(false);
    const currentSymbolIdRef = useRef(null);
    const setSpriteRef = useRef(null); // Always-current ref to setSpriteFromSymbolId (avoids stale closure in imperative handle)
    const handleSymbolStopCompleteRef = useRef(null); // Always-current ref to handleSymbolStopComplete (avoids stale closure in onStop)

    const dispatch = useDispatch();
    const { handleSymbolStopComplete } = useRoyalCollectControl();
    const royalCollectCurrentRound = useSelector(
      selectRoyalCollectCurrentRound,
    );

    // Only the middle reel royal is locked (never spins)
    const isLockedRoyal = useMemo(
      () => reelIndex === 1 && isRoyalSymbol(symbolId),
      [symbolId, reelIndex],
    );

    // Callback ref to track when mask is ready
    const setMaskRef = useCallback(
      (node) => {
        maskRef.current = node;
        if (node && !maskReady) {
          setMaskReady(true);
        }
      },
      [maskReady],
    );

    // Create mask graphics draw function
    const drawMask = useCallback(
      (g) => {
        g.clear();
        g.beginFill(0x000000, 1);
        g.drawRect(
          -symbolWidth / 2,
          -symbolHeight / 2,
          symbolWidth,
          symbolHeight,
        );
        g.endFill();
      },
      [symbolWidth, symbolHeight],
    );

    // Mask element — only for non-bonus blanks (bonus symbols use spine overlays)
    const maskGraphics = useMemo(
      () =>
        !isLockedRoyal ? (
          <Graphics
            ref={setMaskRef}
            name={`rc_symbol_mask_${reelIndex}_${index}`}
            draw={drawMask}
          />
        ) : null,
      [setMaskRef, reelIndex, index, isLockedRoyal, drawMask],
    );

    /**
     * Calculate uniform symbol scale — mirrors ReelControllerV2._calculateSymbolScale exactly.
     * All symbols get the SAME scale factor: containerHeight / Settings.SYMBOL_HEIGHT (168px).
     * Premium symbols with larger sourceSize naturally overflow their cells.
     */
    const calculateScale = useCallback(
      () => {
        if (!symbolHeight) return 1;
        return symbolHeight / Settings.SYMBOL_HEIGHT;
      },
      [symbolHeight],
    );

    /**
     * Get coin display value — mirrors ReelControllerV2._getCoinDisplayValue exactly.
     * symbolValue × bet, formatted with locale.
     */
    const getCoinDisplayValue = useCallback(
      (symbolValue) => {
        if (!symbolValue || symbolValue === null) return "";
        try {
          const coinValue = parseInt(symbolValue, 10);
          if (isNaN(coinValue) || coinValue <= 0) return "";

          const state = store.getState();
          const freeRoundsActive = state?.freeRounds?.status === "active";
          const freeRoundsBet = state?.freeRounds?.betValue || null;
          const bet = freeRoundsActive && freeRoundsBet
            ? freeRoundsBet
            : state.game.bet || 1;

          const displayValue = coinValue * bet;
          return formatToCustomLocale(displayValue);
        } catch {
          return "";
        }
      },
      [],
    );

    /**
     * Set sprite texture + value text from symbolId — mirrors ReelControllerV2._setSpriteTexture.
     *
     * Flow: symbolId → symbolData[symbolId] → texture + value
     */
    const setSpriteFromSymbolId = useCallback(
      (container, targetSymbolId) => {
        if (!container || !symbolsSpriteSheet?.textures) return;

        // Null/undefined symbol — render nothing
        if (targetSymbolId === null || targetSymbolId === undefined) {
          if (staticSpriteRef.current) {
            staticSpriteRef.current.texture = PIXI.Texture.EMPTY;
            staticSpriteRef.current.alpha = 0.001;
          }
          if (valueTextRef.current) {
            valueTextRef.current.text = "";
            valueTextRef.current.alpha = 0;
          }
          currentSymbolIdRef.current = null;
          return;
        }

        // Skip if already showing this symbol AND the texture is valid.
        // After collection, the texture is PIXI.Texture.EMPTY even though
        // currentSymbolIdRef may match — we MUST re-apply the texture.
        if (
          currentSymbolIdRef.current === targetSymbolId &&
          staticSpriteRef.current &&
          staticSpriteRef.current.texture !== PIXI.Texture.EMPTY
        ) {
          return;
        }

        const symbolInfo = symbolData[targetSymbolId];
        if (!symbolInfo) {
          if (staticSpriteRef.current) {
            staticSpriteRef.current.texture = PIXI.Texture.EMPTY;
            staticSpriteRef.current.alpha = 0.001;
          }
          currentSymbolIdRef.current = null;
          return;
        }

        const textureName = symbolInfo.texture;
        const symbolValue = symbolInfo.value;

        // If texture is null/empty, set empty (same as ReelControllerV2 line 353)
        if (!textureName) {
          if (staticSpriteRef.current) {
            staticSpriteRef.current.texture = PIXI.Texture.EMPTY;
            staticSpriteRef.current.alpha = 0.001;
          }
          if (valueTextRef.current) {
            valueTextRef.current.text = "";
            valueTextRef.current.alpha = 0;
          }
          currentSymbolIdRef.current = null;
          return;
        }

        const texture =
          symbolsSpriteSheet.textures[`${textureName}.png`] || null;

        if (!texture) return;

        // Create sprite if doesn't exist
        if (!staticSpriteRef.current) {
          const sprite = new PIXI.Sprite(texture);
          sprite.anchor.set(0.5);
          sprite.name = "rc_symbol_texture";
          sprite.roundPixels = true;
          container.addChild(sprite);
          staticSpriteRef.current = sprite;
        } else {
          staticSpriteRef.current.texture = texture;
        }

        // NOTE: Do NOT set alpha here — the context decides visibility
        // via handleSymbolStopComplete (show for no-spine, hide for spine+overlay)

        // Uniform scale — same as ReelControllerV2._calculateSymbolScale
        const scale = calculateScale();
        staticSpriteRef.current.scale.set(scale);

        currentSymbolIdRef.current = targetSymbolId;

        // --- Coin value text (only for coins, NOT jackpots which display via spine) ---
        if (symbolValue && isCoinSymbol(targetSymbolId)) {
          // Create value text if doesn't exist
          if (!valueTextRef.current && PIXI.BitmapFont.available["font_secondary_scaled"]) {
            const fontSize = symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
            const vt = new PIXI.BitmapText("", {
              fontName: "font_secondary_scaled",
              fontSize: fontSize,
            });
            vt.anchor.set(0.5);
            vt.name = "rc_symbol_value";
            vt.zIndex = 1;
            vt.roundPixels = true;
            container.addChild(vt);
            valueTextRef.current = vt;
          }

          if (valueTextRef.current) {
            const displayValue = getCoinDisplayValue(symbolValue);
            valueTextRef.current.text = displayValue;
            valueTextRef.current.alpha = displayValue !== "" ? 1 : 0;
            valueTextRef.current.y = symbolHeight * COIN_VALUE_Y_OFFSET;

            // Auto-fit scaling (same as ReelControllerV2 lines 416-433)
            if (displayValue !== "") {
              valueTextRef.current.scale.set(1);
              valueTextRef.current.updateText();

              const coinWidth = symbolWidth;
              const paddingPercent = 0.12;
              const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
              const actualTextWidth = valueTextRef.current.width;

              if (actualTextWidth > maxTextWidth) {
                const fitScale = maxTextWidth / actualTextWidth;
                valueTextRef.current.scale.set(fitScale);
              }
            }
          }
        } else if (valueTextRef.current) {
          // Non-coin: clear text
          valueTextRef.current.text = "";
          valueTextRef.current.alpha = 0;
        }
      },
      [symbolsSpriteSheet, symbolWidth, symbolHeight, calculateScale, getCoinDisplayValue],
    );

    // Keep setSpriteRef always pointing to the latest version of setSpriteFromSymbolId.
    // The imperative handle uses [] deps (never re-creates), so direct closure capture
    // would go stale when symbolsSpriteSheet loads after mount.
    useEffect(() => {
      setSpriteRef.current = setSpriteFromSymbolId;
    }, [setSpriteFromSymbolId]);

    // Keep handleSymbolStopCompleteRef pointing to the latest context callback.
    // The onStop callback in the controller init effect has [] deps on spritesheet,
    // so it would capture a stale handleSymbolStopComplete if bet/deps change.
    useEffect(() => {
      handleSymbolStopCompleteRef.current = handleSymbolStopComplete;
    }, [handleSymbolStopComplete]);

    // Phase 1: Create a static sprite to show the initial symbol texture
    useEffect(() => {
      if (!symbolsContainerRef.current || !symbolsSpriteSheet?.textures) return;

      // Clear any existing children
      const container = symbolsContainerRef.current;
      while (container.children.length > 0) {
        container.removeChildAt(0);
      }
      staticSpriteRef.current = null;
      valueTextRef.current = null;

      // Set texture from symbolId using symbolData lookup
      setSpriteFromSymbolId(container, symbolId);

      // Report ready to Redux
      if (!reportedReadyRef.current) {
        dispatch(reportRoyalCollectSymbolReady());
        reportedReadyRef.current = true;
      }

      return () => {
        staticSpriteRef.current = null;
        valueTextRef.current = null;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Controller Initialization (Phase 2) ────────────────────────────
    // Creates the spin engine for blank cells only
    useEffect(() => {
      if (!spinContainerRef.current || !symbolsSpriteSheet?.textures) return;
      if (isLockedRoyal) return; // Locked royal on middle reel doesn't spin

      // Clean up previous controller
      if (controllerRef.current) {
        controllerRef.current.cleanup();
        controllerRef.current = null;
      }

      // Wrap onStop to prepare the static sprite texture for the landed symbol.
      // IMPORTANT: Do NOT show the static sprite here — the context's handleSymbolStopComplete
      // decides whether to show it (blanks) or trigger an overlay landing animation (coins).
      //
      // Uses refs instead of direct closure capture to avoid stale closures:
      // - setSpriteRef.current: always has latest spritesheet + dimensions (fixes resize)
      // - handleSymbolStopCompleteRef.current: always has latest context callback (fixes bet changes)
      const onStop = (symbolIdx, reelIdx) => {
        const ctrl = controllerRef.current;
        if (ctrl && ctrl.symbolId !== undefined && ctrl.symbolId !== null) {
          // Prepare static sprite with the landed symbol texture
          setSpriteRef.current?.(symbolsContainerRef.current, ctrl.symbolId);
        }
        // Report to context — it handles visibility (static texture vs overlay animation)
        handleSymbolStopCompleteRef.current?.(symbolIdx, reelIdx);
      };

      const controller = new RoyalCollectSymbolController(
        spinContainerRef.current,
        symbolId,
        symbolWidth,
        symbolHeight,
        index,
        reelIndex,
        onStop,
        symbolsSpriteSheet,
        symbolsSpriteSheetBlurred,
      );

      controllerRef.current = controller;

      return () => {
        if (controllerRef.current) {
          controllerRef.current.cleanup();
          controllerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [symbolsSpriteSheet, symbolsSpriteSheetBlurred]);

    // Update controller landing data when current round changes.
    // IMPORTANT: Do NOT update the static sprite here — that happens in the onStop callback
    // after the symbol actually lands. Updating it now would make coins appear instantly
    // on top of the blurry spinning animation.
    useEffect(() => {
      if (
        !symbolsContainerRef.current ||
        !symbolsSpriteSheet?.textures ||
        !royalCollectCurrentRound?.reels
      )
        return;

      const reel = royalCollectCurrentRound.reels[reelIndex];
      if (!Array.isArray(reel) || index >= reel.length) return;

      const newSymbolId = reel[index];
      if (newSymbolId === undefined) return;

      // Only set landing data on the controller — the static sprite
      // will be updated in the onStop callback when the symbol actually lands
      if (controllerRef.current && !controllerRef.current.isPermanentRoyal) {
        controllerRef.current.setLandingSymbolId(newSymbolId);
      } else if (!controllerRef.current || controllerRef.current.isPermanentRoyal) {
        // Symbol already landed (bonus) or no controller — update static sprite directly
        setSpriteFromSymbolId(symbolsContainerRef.current, newSymbolId);
      }
    }, [
      royalCollectCurrentRound,
      reelIndex,
      index,
      symbolsSpriteSheet,
      setSpriteFromSymbolId,
    ]);

    // Resize handler — update scale + text position on dimension change
    useEffect(() => {
      if (staticSpriteRef.current && staticSpriteRef.current.texture !== PIXI.Texture.EMPTY) {
        const scale = calculateScale();
        staticSpriteRef.current.scale.set(scale);
      }
      if (valueTextRef.current && valueTextRef.current.text !== "") {
        valueTextRef.current.y = symbolHeight * COIN_VALUE_Y_OFFSET;
        // Re-fit text
        valueTextRef.current.scale.set(1);
        valueTextRef.current.updateText();
        const maxTextWidth = symbolWidth * (1 - 0.12 * 2);
        const actualTextWidth = valueTextRef.current.width;
        if (actualTextWidth > maxTextWidth) {
          valueTextRef.current.scale.set(maxTextWidth / actualTextWidth);
        }
      }

      // Resize controller
      if (controllerRef.current) {
        controllerRef.current.resize(symbolWidth, symbolHeight);
      }
    }, [symbolWidth, symbolHeight, calculateScale]);

    useImperativeHandle(
      ref,
      () => ({
        getSymbolController: () => controllerRef.current,
        isControllerReady: () => reportedReadyRef.current,

        /** Start the spin animation (blank cells only) */
        startSpin: () => {
          const controller = controllerRef.current;
          if (!controller || controller.isPermanentRoyal) return;

          // Hide static sprite during spin
          if (staticSpriteRef.current) {
            staticSpriteRef.current.alpha = 0.001;
          }
          if (valueTextRef.current) {
            valueTextRef.current.alpha = 0;
          }

          controller.startSpin();
        },

        /** Start spin with wind-up animation (AUTO speed only).
         *  Returns a Promise that resolves when wind-up completes and spin starts. */
        startWindUp: () => {
          const controller = controllerRef.current;
          if (!controller || controller.isPermanentRoyal) return Promise.resolve();

          // Hide static sprite — spin sprites take over the visual
          if (staticSpriteRef.current) {
            staticSpriteRef.current.alpha = 0.001;
          }
          if (valueTextRef.current) {
            valueTextRef.current.alpha = 0;
          }

          return controller.startSpinWithWindUp();
        },

        /** Signal the controller to stop spinning */
        stopSpin: () => {
          controllerRef.current?.stopSpin();
        },

        /** Hide static texture + value text (spine overlay takes over) */
        hideSymbolTexture: () => {
          if (staticSpriteRef.current) {
            staticSpriteRef.current.alpha = 0.001;
          }
          if (valueTextRef.current) {
            valueTextRef.current.alpha = 0;
          }
          // Also hide controller's spinning sprites
          if (controllerRef.current) {
            for (let i = 0; i < controllerRef.current.spinningSprites.length; i++) {
              controllerRef.current.spinningSprites[i].container.visible = false;
            }
          }
        },

        /** Show static texture + value text (restore from spine overlay) */
        showSymbolTexture: () => {
          if (staticSpriteRef.current && staticSpriteRef.current.texture !== PIXI.Texture.EMPTY) {
            staticSpriteRef.current.alpha = 1;
          }
          if (valueTextRef.current && valueTextRef.current.text !== "") {
            valueTextRef.current.alpha = 1;
          }
        },

        /** Force-update static sprite texture from symbolId, then show it.
         *  Needed when the previous texture was PIXI.Texture.EMPTY (e.g. after collection)
         *  and the new landing symbol needs its texture restored before becoming visible.
         *  Uses setSpriteRef (not direct closure) to always have the latest spritesheet. */
        updateSymbolTexture: (symId) => {
          setSpriteRef.current?.(symbolsContainerRef.current, symId);
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    // Container Y position (symbol within reel)
    const containerY = useMemo(
      () => symbolHeight * index,
      [symbolHeight, index],
    );

    // Spin container Y offset: aligns the controller's VISIBLE_SYMBOL_INDEX
    // sprite with the mask center (y=0)
    const spinContainerY = useMemo(
      () => -VISIBLE_SYMBOL_INDEX * symbolHeight,
      [symbolHeight],
    );

    return (
      <Container y={containerY} name={`rc_symbol_outer_${reelIndex}_${index}`}>
        {!isLockedRoyal && maskGraphics}
        <Container
          name={`rc_symbol_content_${reelIndex}_${index}`}
          mask={!isLockedRoyal && maskReady ? maskRef.current : null}
        >
          {/* Static sprite container (initial textures, coins, etc.) */}
          <Container
            ref={symbolsContainerRef}
            name={`rc_symbol_sprites_${reelIndex}_${index}`}
            sortableChildren
          />
          {/* Spin container (controller's 4-sprite buffer, offset for mask alignment) */}
          {!isLockedRoyal && (
            <Container
              ref={spinContainerRef}
              name={`rc_symbol_spin_${reelIndex}_${index}`}
              y={spinContainerY}
            />
          )}
        </Container>
      </Container>
    );
  },
);

RoyalCollectSymbol.displayName = "RoyalCollectSymbol";

export default React.memo(RoyalCollectSymbol);
